import {Feed} from '@/api/models/Feeds.ts';
import {Schemas} from '@/api/models/Schema.ts';
import Pending from '@/components/Pending.tsx';
import {Toaster} from '@/toaster.ts';
import {Button, Classes, Dialog} from '@blueprintjs/core';
import {EventHandler, MouseEventHandler, ReactElement, SyntheticEvent, useCallback, useMemo} from 'react';
import Highlight from 'react-syntax-highlighter';
import {dracula} from 'react-syntax-highlighter/dist/esm/styles/hljs';

interface Props {
	feed: Feed,
	onClose: () => void,
}

export default function PreviewDialog({feed, onClose}: Props): ReactElement {
	const preview = useMemo(() => {
		return Schemas.preview(feed.id);
	}, [feed]);

	const onIngest: EventHandler<SyntheticEvent> = useCallback(async event => {
		event.preventDefault();

		await Schemas.ingest(feed.id);
		await Toaster.info(`${feed.name} has been queued for processing. Check back in a few minutes.`);

		onClose();
	}, [feed, onClose]);

	return (
		<Dialog title={`${feed.name} Schema Preview`} onClose={onClose} isOpen={true} style={{width: '50%'}}>
			<div className={Classes.DIALOG_BODY}>
				<Pending resource={preview}>
					{preview => <Content onIngest={onIngest} preview={preview} />}
				</Pending>
			</div>

			<div className={Classes.DIALOG_FOOTER}>
				<div className={Classes.DIALOG_FOOTER_ACTIONS}>
					<Button text="Close" onClick={onClose} />
				</div>
			</div>
		</Dialog>
	);
}

interface ContentProps {
	preview: object | null,
	onIngest: MouseEventHandler<HTMLAnchorElement>,
}

function Content({preview, onIngest}: ContentProps): ReactElement {
	const content = useMemo(() => {
		if (!preview)
			return '';

		return JSON.stringify(preview, null, 4);
	}, [preview]);

	if (!preview) {
		return (
			<>
				<p>No preview available. The feed may not have been ingested yet.</p>
				<p><a href="#" onClick={onIngest}>Click here</a> to queue the feed for immediate processing.</p>
			</>
		);
	}

	return (
		<Highlight language="json" style={dracula}>
			{content}
		</Highlight>
	);
}
